import { createForm } from "@tanstack/solid-form";
import { IoClose, IoFilter } from "solid-icons/io";
import {
  type Component,
  createSignal,
  For,
  Match,
  Show,
  Switch,
} from "solid-js";
import { useI18n } from "@/api/i18n";
import { CurrencyInput, TagInput } from "@/components/shared/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  RadioGroup,
  RadioGroupItem,
  RadioGroupItemControl,
  RadioGroupItemLabel,
} from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  type CategoryOption,
  categoryOption,
  categoryOptions,
} from "@/domains/freelance/taxonomy/store/taxonomy-store";
import { FILTERS_CONFIG, GENERAL_FILTERS } from "../constants/filters";
import { useFilterStore } from "../context/FilterProvider";
import {
  type Duration,
  type Effort,
  PaymentType,
  type RemotePolicy,
  type StartDate,
} from "../create/constants";

interface AdvancedFiltersSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AdvancedFiltersSheet: Component<AdvancedFiltersSheetProps> = (
  props,
) => {
  const [filterStore, setFilterStore] = useFilterStore();
  const [selectedPaymentType, setSelectedPaymentType] = createSignal<
    PaymentType | "all"
  >((filterStore.payment_type as PaymentType | "all") || "all");

  const form = createForm(() => ({
    defaultValues: {
      payment_type: filterStore.payment_type || null,
      daily_rate_min: filterStore.daily_rate_min || null,
      daily_rate_max: filterStore.daily_rate_max || null,
      // TagInput expects string[]; store has comma-separated string — normalize for form
      skills:
        typeof filterStore.skills === "string" && filterStore.skills
          ? filterStore.skills
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : (filterStore.skills ?? null),
      job_category: filterStore.job_category || null,
      experience_level: filterStore.experience_level || null,
      budget: filterStore.budget || null,
      duration: filterStore.duration || null,
      effort: filterStore.effort || null,
      renewable: filterStore.renewable?.toString() || "all",
      start_date: filterStore.start_date || null,
      remote_policy: filterStore.remote_policy || null,
      payment_status: filterStore.payment_status || null,
      project_type: filterStore.project_type || null,
    },
    onSubmit: (data) => {
      const renewable =
        data.value.renewable === "all" ? null : data.value.renewable === "true";

      // Normalize skills to comma-separated string (same as job_category) so URL params are skills=a,b,c not skills=a&skills=b
      const skills: string | null =
        Array.isArray(data.value.skills) && data.value.skills.length > 0
          ? data.value.skills.join(",")
          : typeof data.value.skills === "string"
            ? data.value.skills
            : null;

      const { skills: _skip, ...rest } = data.value;
      setFilterStore({
        ...rest,
        skills,
        renewable: renewable,
      });
      props.onOpenChange(false);
    },
  }));

  const handlePaymentTypeChange = (value: PaymentType | "all") => {
    setSelectedPaymentType(value);
    form.setFieldValue("payment_type", value === "all" ? null : value);

    // Clear all payment-related fields when payment type changes to ensure consistency
    if (value === "all") {
      // Clear all payment-related fields when "all" is selected
      form.setFieldValue("daily_rate_min", null);
      form.setFieldValue("daily_rate_max", null);
      form.setFieldValue("budget", null);
    } else if (value === PaymentType.FIXED) {
      // Clear hourly-specific fields when switching to fixed
      form.setFieldValue("daily_rate_min", null);
      form.setFieldValue("daily_rate_max", null);
    } else if (value === PaymentType.DAILY) {
      // Clear fixed-specific fields when switching to daily
      form.setFieldValue("budget", null);
    }
  };

  const { t } = useI18n();

  return (
    <Sheet open={props.isOpen} onOpenChange={props.onOpenChange}>
      <SheetContent
        side="right"
        class="lg:max-w-2/5 sm:max-w-none max-w-full w-full bg-card"
      >
        <SheetHeader>
          <SheetTitle class="flex items-center gap-2">
            <IoFilter size={20} />
            {t("home.filter_sheet.title")}
          </SheetTitle>
        </SheetHeader>

        <div class="min-h-[300px] flex flex-col gap-2 p-2">
          {/* Payment Type Section */}
          <div class="bg-matisse-50 rounded-lg p-4">
            <h3 class="text-lg font-semibold text-primary mb-2">
              {t("home.filter_sheet.payment_type.title")}
            </h3>

            <form.Field name="payment_type">
              {(field) => (
                <Select
                  value={selectedPaymentType()}
                  options={["all", PaymentType.DAILY, PaymentType.FIXED]}
                  onChange={(value) => {
                    handlePaymentTypeChange(value as PaymentType | "all");
                    field().handleChange(value);
                  }}
                  itemComponent={(props) => (
                    <SelectItem {...props}>
                      {t(`common.payment_type_options.${props.item.rawValue}`)}
                    </SelectItem>
                  )}
                >
                  <SelectTrigger class="w-full bg-white">
                    <SelectValue>
                      {t(
                        `common.payment_type_options.${selectedPaymentType()}`,
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent />
                </Select>
              )}
            </form.Field>
            <div class="mt-2 p-2 bg-matisse-100 rounded text-sm text-primary">
              {
                t(
                  `home.filter_sheet.payment_type.description_${selectedPaymentType()}`,
                ) as string
              }
            </div>
          </div>

          {/* Conditional Salary Section */}
          <Show when={selectedPaymentType() === PaymentType.DAILY}>
            <div class="bg-matisse-50 rounded-lg p-4">
              <div class="flex items-center gap-2 mb-3">
                <div class="w-6 h-6 bg-matisse-500 rounded-full flex items-center justify-center">
                  <span class="text-white text-xs font-bold">€</span>
                </div>
                <h3 class="text-lg font-semibold text-primary">
                  {t("home.filter_sheet.daily_filters.title")}
                </h3>
              </div>
              <div class="flex flex-col lg:flex-row gap-2">
                <div class="space-y-3 w-full">
                  <div>
                    <p class="text-sm font-medium text-gray-700 mb-1 block">
                      {t(
                        "home.filter_sheet.daily_filters.daily_rate_min.title",
                      )}
                    </p>
                    <form.Field name="daily_rate_min">
                      {(field) => (
                        <CurrencyInput
                          fieldApi={field()}
                          value={field().state.value ?? ""}
                          placeholder={t(
                            "home.filter_sheet.daily_filters.daily_rate_min.placeholder",
                          )}
                          class="w-full"
                        />
                      )}
                    </form.Field>
                  </div>
                </div>
                <div class="space-y-3 w-full">
                  <div>
                    <p class="text-sm font-medium text-gray-700 mb-1 block">
                      {t(
                        "home.filter_sheet.daily_filters.daily_rate_max.title",
                      )}
                    </p>
                    <form.Field name="daily_rate_max">
                      {(field) => (
                        <CurrencyInput
                          fieldApi={field()}
                          value={field().state.value ?? ""}
                          placeholder={t(
                            "home.filter_sheet.daily_filters.daily_rate_max.placeholder",
                          )}
                          class="w-full"
                        />
                      )}
                    </form.Field>
                  </div>
                </div>
              </div>
            </div>
          </Show>
          <Show when={selectedPaymentType() === PaymentType.FIXED}>
            <div class="bg-matisse-50 rounded-lg p-4">
              <div class="flex items-center gap-2 mb-3">
                <div class="w-6 h-6 bg-matisse-500 rounded-full flex items-center justify-center">
                  <span class="text-white text-xs font-bold">€</span>
                </div>
                <h3 class="text-lg font-semibold text-primary">
                  {t("home.filter_sheet.fixed_filters.title")}
                </h3>
              </div>

              <div class="space-y-3 w-full">
                <div>
                  <p class="text-sm font-medium text-gray-700 mb-1 block">
                    {t("home.filter_sheet.fixed_filters.budget.title")}
                  </p>
                  <form.Field name="budget">
                    {(field) => (
                      <CurrencyInput
                        fieldApi={field()}
                        value={field().state.value ?? ""}
                        placeholder={t(
                          "home.filter_sheet.fixed_filters.budget.placeholder",
                        )}
                        class="w-full"
                      />
                    )}
                  </form.Field>
                </div>
              </div>
            </div>
          </Show>

          {/* General Filters Section */}
          <div class="bg-gray-50 rounded-lg p-4">
            <div class="flex items-center gap-2 mb-3">
              <div class="w-6 h-6 bg-gray-500 rounded-full flex items-center justify-center">
                <span class="text-white text-xs font-bold">⚙️</span>
              </div>
              <h3 class="text-lg font-semibold text-gray-700">
                {t("home.filter_sheet.general_filters.title")}
              </h3>
            </div>

            <div class="space-y-4">
              <For each={GENERAL_FILTERS}>
                {(filter) => (
                  <div class="py-1 last:border-b-0 last:border-gray-200">
                    <p class="text-sm font-medium text-gray-700 mb-2">
                      {FILTERS_CONFIG[filter].label}
                    </p>
                    <Switch>
                      {/* The sectors are chosen by Italian label but filter
                          by English key: the value is `CategoryOption`, the
                          CSV the field holds is of keys. */}
                      <Match when={filter === "job_category"}>
                        <form.Field name="job_category">
                          {(field) => {
                            // From the CSV of keys, not from Kobalte's selected
                            // options: Kobalte drops a key outside `options` (an
                            // old URL), which must still show as a chip.
                            const chosen = () =>
                              (field().state.value ?? "")
                                .split(",")
                                .filter(Boolean)
                                .map((key) => categoryOption(key));
                            return (
                              <Select<CategoryOption>
                                value={chosen()}
                                options={categoryOptions()}
                                optionValue="value"
                                optionTextValue="label"
                                multiple
                                itemComponent={(props) => (
                                  <SelectItem item={props.item}>
                                    {props.item.rawValue.label}
                                  </SelectItem>
                                )}
                                onChange={(options) => {
                                  field().handleChange(
                                    options.map((o) => o.value).join(","),
                                  );
                                }}
                                placeholder={FILTERS_CONFIG[filter].placeholder}
                              >
                                <SelectTrigger class="h-full bg-white">
                                  <SelectValue<CategoryOption>>
                                    {() => (
                                      <div class="flex flex-wrap gap-2">
                                        <For each={chosen()}>
                                          {(option) => (
                                            <Badge
                                              variant="secondary"
                                              class="cursor-pointer"
                                              onPointerDown={(e) =>
                                                e.stopPropagation()
                                              }
                                            >
                                              {option.label}
                                              {/* Not a button: this sits inside the
                                                select's trigger, a button of its own.
                                                By keyboard, an option is removed by
                                                choosing it again in the list. */}
                                              <span
                                                aria-hidden="true"
                                                onClick={() => {
                                                  field().handleChange(
                                                    chosen()
                                                      .filter(
                                                        (o) =>
                                                          o.value !==
                                                          option.value,
                                                      )
                                                      .map((o) => o.value)
                                                      .join(","),
                                                  );
                                                }}
                                              >
                                                <IoClose size={16} />
                                              </span>
                                            </Badge>
                                          )}
                                        </For>
                                      </div>
                                    )}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent class="max-h-[160px]" />
                              </Select>
                            );
                          }}
                        </form.Field>
                      </Match>
                      <Match when={FILTERS_CONFIG[filter].type === "array"}>
                        <form.Field name={filter}>
                          {(field) => (
                            <Select<string>
                              value={
                                (
                                  field().state.value as string | undefined
                                )?.split(",") ?? []
                              }
                              options={
                                FILTERS_CONFIG[filter].options as string[]
                              }
                              multiple
                              itemComponent={(props) => (
                                <Switch
                                  fallback={
                                    <SelectItem {...props}>
                                      {props.item.rawValue}
                                    </SelectItem>
                                  }
                                >
                                  <Match
                                    when={
                                      FILTERS_CONFIG[filter].key === "duration"
                                    }
                                  >
                                    <SelectItem item={props.item}>
                                      {t(
                                        `common.duration_options.${props.item.rawValue as Duration}`,
                                      )}
                                    </SelectItem>
                                  </Match>
                                  <Match
                                    when={
                                      FILTERS_CONFIG[filter].key === "effort"
                                    }
                                  >
                                    <SelectItem {...props}>
                                      {t(
                                        `common.effort_options.${props.item.rawValue as Effort}`,
                                      )}
                                    </SelectItem>
                                  </Match>
                                  <Match
                                    when={
                                      FILTERS_CONFIG[filter].key ===
                                      "remote_policy"
                                    }
                                  >
                                    <SelectItem {...props}>
                                      {t(
                                        `common.remote_policy_options.${props.item.rawValue as RemotePolicy}`,
                                      )}
                                    </SelectItem>
                                  </Match>
                                  <Match
                                    when={
                                      FILTERS_CONFIG[filter].key ===
                                      "start_date"
                                    }
                                  >
                                    <SelectItem {...props}>
                                      {t(
                                        `common.start_date_options.${props.item.rawValue as StartDate}`,
                                      )}
                                    </SelectItem>
                                  </Match>
                                </Switch>
                              )}
                              onChange={(values) => {
                                field().handleChange(values.join(","));
                              }}
                              placeholder={FILTERS_CONFIG[filter].placeholder}
                            >
                              <SelectTrigger class="h-full bg-white">
                                <SelectValue>
                                  {(state) => (
                                    <div class="flex flex-wrap gap-2">
                                      <For each={state.selectedOptions()}>
                                        {(option) => (
                                          <Badge
                                            variant="secondary"
                                            class="cursor-pointer"
                                            onPointerDown={(e) =>
                                              e.stopPropagation()
                                            }
                                          >
                                            <Show
                                              when={
                                                FILTERS_CONFIG[filter].key !==
                                                "duration"
                                              }
                                              fallback={
                                                <p>
                                                  {t(
                                                    `common.duration_options.${option as Duration}`,
                                                  )}
                                                </p>
                                              }
                                            >
                                              {option as string}
                                            </Show>
                                            {/* Not a button: this sits inside the
                                                select's trigger, a button of its own.
                                                By keyboard, an option is removed by
                                                choosing it again in the list. */}
                                            <span
                                              aria-hidden="true"
                                              onClick={() => {
                                                state.remove(option);
                                                field().handleChange(
                                                  state
                                                    .selectedOptions()
                                                    .join(","),
                                                );
                                              }}
                                            >
                                              <IoClose size={16} />
                                            </span>
                                          </Badge>
                                        )}
                                      </For>
                                    </div>
                                  )}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent class="max-h-[160px]" />
                            </Select>
                          )}
                        </form.Field>
                      </Match>
                      <Match when={FILTERS_CONFIG[filter].type === "tag"}>
                        <form.Field name={filter}>
                          {(field) => (
                            <TagInput fieldApi={field()} class="bg-white" />
                          )}
                        </form.Field>
                      </Match>
                      <Match
                        when={FILTERS_CONFIG[filter].type === "radio-group"}
                      >
                        <Show when={filter === "renewable"}>
                          <form.Field name={filter}>
                            {(field) => (
                              <RadioGroup
                                value={
                                  (field().state.value as string | undefined) ??
                                  undefined
                                }
                                onChange={(value) => {
                                  field().handleChange(value);
                                }}
                                class="flex gap-2"
                              >
                                <For each={FILTERS_CONFIG[filter].options}>
                                  {(option) => (
                                    <RadioGroupItem
                                      value={option}
                                      class="flex items-center gap-2 w-full"
                                    >
                                      <RadioGroupItemControl />
                                      <RadioGroupItemLabel>
                                        {
                                          t(
                                            `home.filter_sheet.general_filters.renewable_options.${option}`,
                                          ) as string
                                        }
                                      </RadioGroupItemLabel>
                                    </RadioGroupItem>
                                  )}
                                </For>
                              </RadioGroup>
                            )}
                          </form.Field>
                        </Show>
                      </Match>
                    </Switch>
                  </div>
                )}
              </For>
            </div>
          </div>

          {/* Action Buttons */}
          <div class="flex justify-end pt-4">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                form.reset();
                setFilterStore({
                  renewable: null,
                  start_date: null,
                  remote_policy: null,
                  payment_type: null,
                  daily_rate_min: null,
                  daily_rate_max: null,
                  skills: null,
                  job_category: null,
                  experience_level: null,
                  budget: null,
                  duration: null,
                  effort: null,
                  project_type: null,
                });
                setSelectedPaymentType("all");
                props.onOpenChange(false);
              }}
            >
              Reset filtri
            </Button>
            <Button
              size="sm"
              onClick={() => {
                form.handleSubmit();
              }}
            >
              Applica filtri
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
